import { toast } from "react-toastify";

import firestore from "src/apiService/firestore";
import { getUserId } from "src/config/storage";
import {
  userPlanSelector,
  userGetInventoryTypeSelector,
  effectiveUserTimeZoneSelector,
  isSupportUserSessionSelector,
  isImpersonationSessionSelector,
  featureFlagSelector,
} from "src/store/system/selector";
import { getPoshmarkIntegrations } from "src/store/plaidIntegration/selector";
import { lastDownloadTypeSelector } from "src/store/uploads/selector";
import { poshmarkIntegrationUpdate } from "src/apiService/modules/poshmark";
import {
  ingestMappedPendingInventoryFile,
  deletePendingInventoryByListingIds,
} from "src/apiService/modules/pendingInventory";
import { getPoshmarkUser } from "src/utils/extension/poshmark";
import type { PoshmarkIntegration } from "src/interfaces/plaidIntegration.interface";
import type MonthYear from "src/interfaces/monthYear.interface";
import parallelize from "src/utils/parallelize";
import Cacher from "src/utils/Cacher";
import FirebaseBatcher from "src/utils/FirebaseBatcher";
import { stampNormalizedMatchFields } from "src/utils/normalizeMatchKey";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";
import { uploadFile } from "src/utils/storage";
import {
  endOfMonthInTimeZone,
  startOfMonthInTimeZone,
} from "src/utils/timezone";

import type { Dispatcher, SyncStatus, ErrorSyncStatusCode } from "../../types";
import { updateSyncStatus as _updateSyncStatus } from "../actions";
import {
  isUserAllowedToDirectImport,
  saveSyncDate,
  getLastSyncDate,
  setBeforeUnloadHandler,
  clearBeforeUnloadHandler,
  ReSyncInterval,
  RunningKeys,
  shouldRunDailySync,
  checkIfItIsInitialImport,
  checkExtensionOrDispatchError,
  notifySyncAlreadyLocked,
} from "../utils";
import { withSyncLock, type SyncLock } from "../syncLock";

import {
  computeMinSaleDate,
  computeMaxSaleDate,
  finalizeSaleComputedFields,
} from "src/utils/sales";

import { mapSale, factorReturnedSale, mapInventory } from "./mappings";
import PoshmarkClient, {
  NotLoggedError,
  PoshmarkRequestSessionExpiredError,
  logPoshmarkError,
  poshmarkUserFacingErrorMessage,
} from "./PoshmarkClient";

function updateSyncStatus(status: SyncStatus) {
  return _updateSyncStatus({
    integration: "poshmark" as const,
    status,
  });
}

const ParalelizeJobs = 10;
const MinDownloadWaitTime = 10 * 60 * 1000; // 10 min

async function updateIntegration(integrationId: string) {
  try {
    const data = await getPoshmarkUser();

    if (data.cookies?.length) {
      await poshmarkIntegrationUpdate({
        integrationId,
        cookies: data.cookies,
      });
    }
  } catch (err) {
    console.error("There was an error:", err);
  }
}

async function syncSale(
  uid: string,
  client: PoshmarkClient,
  item: any,
  file: string,
  bankAccount: ReturnType<typeof bankAccountByIntegrationIdSelector>,
  lock: SyncLock
) {
  lock.throwIfAborted();
  const db = firestore();
  const transformed = mapSale(item);
  const transactionId = transformed[0].transaction_id;
  if (transactionId) {
    const snapshot = await db
      .collection("Sales")
      .where("user", "==", uid)
      .where("transaction_id", "==", transactionId)
      .get();

    if (!snapshot.empty) return;
  }

  for (const i of transformed) {
    lock.throwIfAborted();
    const doc = db.collection("Sales").doc();
    const s = {
      ...i,
      id: doc.id,
      user: uid,
      unreviewed: true,
      file,

      import_type: "daily-sync",
      import_platform: "poshmark",
      import_date: new Date(),
    };

    if (bankAccount) {
      s.sale_platform_account_id = bankAccount.id;
      s.sale_platform = bankAccount.name;
    }

    if (item?.details?.order?.display_status === "Order Cancelled") {
      const r = factorReturnedSale(s, item);
      const d = db.collection("Sales").doc();
      s.return_id = d.id;
      await d.set(
        finalizeSaleComputedFields({
          ...r,
          id: d.id,
          user: uid,
          unreviewed: true,
          file,
          import_type: "daily-sync",
          import_platform: "poshmark",
          import_date: new Date(),
        })
      );
    }

    await doc.set(finalizeSaleComputedFields(s));
  }
}

async function syncSales(
  client: PoshmarkClient,
  integration: PoshmarkIntegration,
  date: Date,
  bankAccount: ReturnType<typeof bankAccountByIntegrationIdSelector>,
  lock: SyncLock
): Promise<void> {
  const lastDate = getLastSyncDate(
    integration,
    "poshmarkLastSyncDateTransactions"
  );
  const poshmarkUserId = integration.uid;
  const uid = await getUserId();
  const timestamp = date.valueOf();
  const filename = `${uid}/poshmarkSales/${timestamp}_${integration.id}.json`;
  const allSales: any[] = [];

  for await (const { sales } of client.fetchSalesFull(poshmarkUserId, {
    boughtDate: {
      start: lastDate,
    },
  })) {
    allSales.push(...sales);
    for (const sale of sales) {
      lock.throwIfAborted();
      sale.details = await client.fetchSaleDetail(sale.id);
      if (sale.details?.order?.line_items) {
        for (const lineItem of sale.details.order.line_items) {
          lineItem.product = await client.fetchInventoryItem(
            lineItem.product_id
          );
        }
      }

      await syncSale(uid, client, sale, filename, bankAccount, lock);
    }
  }

  await uploadFile(filename, JSON.stringify(allSales));
}

function getCancelledThresholdDate(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 3);
  return d.toISOString().split("T")[0];
}

function findClosestAndRemove(sales, sale) {
  let index: number | undefined = undefined;
  let salePrice = undefined;
  for (let i = 0; i < sales.length; i++) {
    const current = sales[i];
    if (salePrice !== undefined) {
      const diff = Math.abs(salePrice - sale.sale_price);
      const cDiff = Math.abs(current.sale_price - sale.sale_price);
      if (cDiff > diff) continue;
    }

    index = i;
    salePrice = current.sale_price;
  }

  if (index !== undefined) return sales.splice(index, 1)[0];
}

async function syncReturns(
  client: PoshmarkClient,
  integration: PoshmarkIntegration,
  date: Date,
  lock: SyncLock
): Promise<void> {
  const uid = await getUserId();
  const db = firestore();
  const lastDate = getLastSyncDate(
    integration,
    "poshmarkLastSyncDateTransactions"
  );
  const poshmarkUserId = integration.uid;
  const cancelledThresholdDate = getCancelledThresholdDate();
  const filename = `${uid}/poshmarkSales/${date.valueOf()}_${
    integration.id
  }_returns.json`;
  const all: any[] = [];

  for await (const { sales: returns } of client.fetchSalesFull(poshmarkUserId, {
    status: ["cancelled"],
  })) {
    all.push(...returns);
    for (const ret of returns) {
      lock.throwIfAborted();
      ret.details = await client.fetchSaleDetail(ret.id);

      if (ret.details?.order?.line_items) {
        for (const lineItem of ret.details.order.line_items) {
          lineItem.product = await client.fetchInventoryItem(
            lineItem.product_id
          );
        }
      }

      if (ret?.details?.order?.cancelled_on < lastDate) continue;
      if (ret?.details?.order?.display_status !== "Order Cancelled") continue;
      const transformed = mapSale(ret);
      const transactionId = transformed[0].transaction_id;
      if (!transactionId) continue;
      const snapshot = await db
        .collection("Sales")
        .where("user", "==", uid)
        .where("transaction_id", "==", transactionId)
        .get();
      const snapshotData = snapshot.docs.map((s) => s.data());
      if (snapshotData.some((s) => s.is_return)) continue;
      for (const i of transformed) {
        lock.throwIfAborted();
        const doc = db.collection("Sales").doc();
        const s = findClosestAndRemove(snapshotData, i) || i;
        const r = factorReturnedSale(s, ret);
        await doc.set(
          finalizeSaleComputedFields({
            ...r,
            id: doc.id,
            user: uid,
            unreviewed: true,
            sync_start: date.toISOString(),
            filename,
            import_type: "daily-sync",
            import_platform: "poshmark",
            import_date: new Date(),
          })
        );

        if (s.id) {
          await db.collection("Sales").doc(s.id).update({
            in_process: false,
            in_process_date: new Date(),
            return_id: doc.id,
          });
        }
      }
    }

    if (returns.some((s) => s.inventory_booked_at < cancelledThresholdDate))
      break;
  }

  await uploadFile(filename, JSON.stringify(all));
}

async function syncInProcess(
  client: PoshmarkClient,
  integration: PoshmarkIntegration,
  date: Date,
  lock: SyncLock
) {
  const uid = await getUserId();
  const db = firestore();
  const filename = `${uid}/poshmarkSales/${date.valueOf()}_${
    integration.id
  }_inprocess.json`;
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", uid)
    .where("in_process", "==", true)
    .get();
  if (snapshot.empty) return;
  const all: any[] = [];

  for (const doc of snapshot.docs) {
    lock.throwIfAborted();
    const data = doc.data();
    if (!data.transaction_id) continue;
    if (
      !data.import_platform ||
      data.import_platform?.toLowerCase() !== "poshmark"
    )
      continue;
    if (data.is_return) {
      // Returns shouldn't be marked as "in process"
      // Update that flag and ignore it
      console.warn(
        "poshmark sales daily sync found an in process return (in process)",
        {
          id: doc.id,
          data,
        }
      );

      await doc.ref.update({
        in_process: false,
        in_process_date: new Date(),
      });
      continue;
    }

    try {
      const details = await client.fetchSaleDetail(data.transaction_id);
      all.push(details);
      const status = (details.order.display_status || "").trim().toLowerCase();
      const state = (details.order.state || "").trim().toLowerCase();
      if (state === "cancelled" || status === "order complete") {
        const update: {
          in_process: boolean;
          in_process_date: Date;
          return_id?: string;
          in_process_filename: string;
        } = {
          in_process: false,
          in_process_date: new Date(),
          in_process_filename: filename,
        };
        if (
          (details?.order?.state || "").trim().toLowerCase() === "cancelled"
        ) {
          const ret = db.collection("Sales").doc();
          update.return_id = ret.id;
          const r = factorReturnedSale(data, {
            details: details,
          });
          await ret.set(
            finalizeSaleComputedFields({
              ...r,
              id: ret.id,
              user: uid,
              unreviewed: true,
              sync_start: date.toISOString(),
              original_id: data.id,
              import_type: "daily-sync",
              import_platform: "poshmark",
              import_date: new Date(),
              filename,
            })
          );
        }
        await db.collection("Sales").doc(data.id).update(update);
      }
    } catch (error) {
      console.trace(
        error,
        `id: ${data?.id}`,
        `trasaction id: ${data?.transaction_id}`
      );
    }
  }

  await uploadFile(filename, JSON.stringify(all));
}

async function syncReturnDate(
  client: PoshmarkClient,
  integration: PoshmarkIntegration,
  date: Date,
  lock: SyncLock
) {
  const uid = await getUserId();
  const db = firestore();
  const filename = `${uid}/poshmarkSales/${date.valueOf()}_${
    integration.id
  }_returndate.json`;
  const snapshot = await db
    .collection("Sales")
    .where("user", "==", uid)
    .where("return_date_invalid", "==", true)
    .get();
  if (snapshot.empty) return;
  const all: any[] = [];

  for (const doc of snapshot.docs) {
    lock.throwIfAborted();
    const data = doc.data();
    if (!data.transaction_id) continue;
    if (
      !data.import_platform ||
      data.import_platform?.toLowerCase() !== "poshmark"
    )
      continue;
    if (!data.is_return) {
      // Sales shouldn't be marked as "return_date_invalid"
      // Update that flag and ignore it
      console.warn(
        "poshmark sales daily sync found a sale with return_date_invalid",
        {
          id: doc.id,
          data,
        }
      );

      await doc.ref.update({ return_date_invalid: false });
      continue;
    }

    try {
      const details = await client.fetchSaleDetail(data.transaction_id);
      all.push(details);
      const returnDate = new Date(details.order?.cancelled_on);
      if (
        details.order?.cancelled_on &&
        returnDate &&
        returnDate.valueOf() &&
        !isNaN(returnDate.valueOf())
      ) {
        const merged = { ...data, return_date: returnDate };
        await db
          .collection("Sales")
          .doc(data.id)
          .update({
            return_date: returnDate,
            return_date_invalid: false,
            return_date_filename: filename,
            min_date: computeMinSaleDate(merged),
            max_date: computeMaxSaleDate(merged),
          });
      }
    } catch (error) {
      console.trace(
        error,
        `id: ${data?.id}`,
        `trasaction id: ${data?.transaction_id}`
      );
    }
  }

  await uploadFile(filename, JSON.stringify(all));
}

function getISODate(str) {
  try {
    const d = new Date(str);
    return d.toISOString();
  } catch (error) {
    console.warn("Couldn't convert str into date's ISO String", {
      error,
      msg: (error as Error).toString(),
      stack: (error as Error).stack,
      str,
    });
    return str;
  }
}

async function syncInventory(
  client: PoshmarkClient,
  integration: PoshmarkIntegration,
  date: Date,
  lock: SyncLock,
  pendingInventoryEnabled: boolean
): Promise<void> {
  const db = firestore();
  const lastDate = getLastSyncDate(
    integration,
    "poshmarkLastSyncDateInventory"
  );
  const poshmarkUserResponse = await client.fetchUser(integration.uid);
  const uid = await getUserId();
  const poshmarkUsername = poshmarkUserResponse.data.username;
  const all: any[] = [];
  const its = [] as ReturnType<typeof mapInventory>;
  for await (const { items } of client.fetchInventoryFull(poshmarkUsername, {
    sortBy: "added_desc",
    inventoryStatus: ["available"],
  })) {
    if (!items) continue;
    for (const poshmarkItem of items) {
      lock.throwIfAborted();
      if (getISODate(poshmarkItem.first_published_at) < lastDate) continue;
      all.push(poshmarkItem);

      const mappedItems = mapInventory(poshmarkItem, {
        includeZeroQuantity: pendingInventoryEnabled,
      });
      for (const item of mappedItems) {
        its.push(item);
      }
    }
  }

  if (its.length) {
    lock.throwIfAborted();
    const timestamp = date.valueOf();
    const filename = `${uid}/poshmarkInventory/${timestamp}_${integration.id}.json`;
    await uploadFile(filename, JSON.stringify(its));
    await uploadFile(
      `${uid}/poshmarkInventory/${timestamp}_${integration.id}_data.json`,
      JSON.stringify(all)
    );
    if (pendingInventoryEnabled) {
      await ingestMappedPendingInventoryFile({
        filename,
        import_platform: "poshmark",
        integrationId: integration.id,
        timestamp,
      });
    } else {
      const doc = db.collection("Downloads").doc();
      await doc.set({
        day: date.getDate(),
        month: date.getMonth() + 1,
        year: date.getFullYear(),
        filename,
        new: true,
        timestamp,
        type: "poshmarkInventory",
        user: uid,
        integrationId: integration.id,
        integrationName: poshmarkUsername,
      });
    }
  }
}

function syncDaily(
  integrationId: string,
  inventory: boolean,
  dispatcher: Dispatcher
) {
  return async (dispatch, getState): Promise<boolean> => {
    const started = await withSyncLock(
      integrationId,
      RunningKeys,
      async (lock) => {
        const db = firestore();
        const start = new Date();
        const getIntegration = () => {
          const integration = getPoshmarkIntegrations(getState()).find(
            ({ id }) => id === integrationId
          );

          if (!integration)
            throw Error(`Integration not found ${integrationId}`);

          return integration;
        };
        const client = new PoshmarkClient();
        const cookieUserId = await client.getUserId();

        if (cookieUserId !== getIntegration().uid) throw new NotLoggedError();

        await updateIntegration(integrationId);

        const bankAccount = bankAccountByIntegrationIdSelector(
          getState(),
          integrationId
        );

        // Sales
        dispatch(updateSyncStatus({ type: "syncing", step: "sales" }));
        await syncSales(client, getIntegration(), start, bankAccount, lock);

        // Returns
        dispatch(updateSyncStatus({ type: "syncing", step: "returns" }));
        await syncReturns(client, getIntegration(), start, lock);

        const syncDate = new Date(start);
        syncDate.setDate(syncDate.getDate() - 1);
        lock.throwIfAborted();
        await saveSyncDate(
          integrationId,
          "poshmarkLastSyncDateTransactions",
          syncDate.toISOString()
        );

        // In Process Sales
        dispatch(updateSyncStatus({ type: "syncing", step: "in-process" }));
        await syncInProcess(client, getIntegration(), start, lock);

        // Return Date sync
        dispatch(updateSyncStatus({ type: "syncing", step: "return-date" }));
        await syncReturnDate(client, getIntegration(), start, lock);

        if (inventory) {
          dispatch(updateSyncStatus({ type: "syncing", step: "inventory" }));
          if (
            dispatcher !== "auto" ||
            (lastDownloadTypeSelector(getState(), "poshmarkInventory")
              ?.timestamp || 0) +
              MinDownloadWaitTime <
              Date.now()
          ) {
            await syncInventory(
              client,
              getIntegration(),
              start,
              lock,
              !!featureFlagSelector(getState(), "pendingInventory")
            );
            lock.throwIfAborted();
            await saveSyncDate(
              integrationId,
              "poshmarkLastSyncDateInventory",
              syncDate.toISOString()
            );
          }
        }

        lock.throwIfAborted();
        await db.collection("Plaid_Integrations").doc(integrationId).update({
          lastSync: start.toISOString(),
        });
        await updateIntegration(integrationId);
      }
    );

    if (!started) {
      notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Poshmark");
    }
    return started;
  };
}

export function poshmarkSync(
  integration: PoshmarkIntegration,
  dispatcher: Dispatcher
) {
  return async (dispatch, getState) => {
    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Poshmark",
      dispatcher
    );
    if (!extensionId) return;

    const inventoryType = userGetInventoryTypeSelector(getState());
    const shouldDisableInventory = inventoryType === "cash";

    const inventory =
      integration.inventory !== false && !shouldDisableInventory;

    setBeforeUnloadHandler("Poshmark");

    try {
      const started = await dispatch(
        syncDaily(integration.id, inventory, dispatcher)
      );
      if (started) dispatch(updateSyncStatus(undefined));
    } catch (e) {
      logPoshmarkError(e);
      let code: ErrorSyncStatusCode = "unknown";

      if (
        e instanceof NotLoggedError ||
        e instanceof PoshmarkRequestSessionExpiredError
      ) {
        code = "user-not-logged-in";
      }

      const message = poshmarkUserFacingErrorMessage(e);

      dispatch(
        updateSyncStatus({
          type: "error",
          message,
          code,
          dispatcher,
        })
      );
    } finally {
      clearBeforeUnloadHandler("Poshmark");
    }
  };
}

export function poshmarkSyncWithCheck(
  integrationId: string,
  noTimeCheck = false
) {
  return async (dispatch, getState) => {
    const state = getState();
    // Accountant / support impersonation sessions cannot trigger client-side syncs.
    if (isImpersonationSessionSelector(state)) return;
    const plan = userPlanSelector(state);

    if (plan === "ultimate") {
      const integration = getPoshmarkIntegrations(state)?.find(
        ({ id }) => id === integrationId
      );

      if (
        integration &&
        integration.type === "poshmark" &&
        integration.sync &&
        !integration.error
      ) {
        const shouldRun =
          noTimeCheck ||
          (!isSupportUserSessionSelector(state) &&
            (await shouldRunDailySync(integration.id, ReSyncInterval)));

        if (shouldRun) {
          await dispatch(
            poshmarkSync(integration, noTimeCheck ? "manual" : "auto")
          );
        }
      }
    }
  };
}

export function poshmarkSyncInventory(integrationId: string) {
  return async (
    dispatch,
    getState
  ): Promise<{ error?: true; success?: true }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "poshmarkInventory"))
      return { error: true };
    const inventoryType = userGetInventoryTypeSelector(getState());
    if (inventoryType === "cash") return { error: true };

    const integration = getPoshmarkIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "poshmark" || integration.error)
      return { error: true };

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Poshmark",
      "manual"
    );
    if (!extensionId) return { error: true };

    const client = new PoshmarkClient();
    let cookieUserId;
    try {
      cookieUserId = await client.getUserId();
    } catch (e) {
      console.warn(e);
    }

    if (cookieUserId !== integration.uid) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Poshmark with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["inventoryIsRunning" as const];
    setBeforeUnloadHandler("Poshmark");

    const isInitialImport = checkIfItIsInitialImport(
      state,
      "poshmarkInventory"
    );
    let lockBlocked = false;

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          dispatch(
            updateSyncStatus({
              type: "syncing",
              step: "direct-import-inventory",
            })
          );
          const uid = await getUserId();
          const timestamp = Date.now();
          const filename = `${uid}/poshmarkInventory/${timestamp}_${integrationId}.json`;
          const poshmarkUserResponse = await client.fetchUser(integration.uid);
          const poshmarkUsername = poshmarkUserResponse?.data?.username;
          if (!poshmarkUsername) throw new Error("Can't get poshmark user");
          const upload = db.collection("Uploads").doc();
          await upload.set({
            user: uid,
            type: "inventory",
            provider: "poshmark",
            filename,
            n: 0,
            timestamp,
            id: upload.id,
          });
          let docs = 0;
          const importedListingIds: string[] = [];

          const batch = new FirebaseBatcher();
          const cache = new Cacher<string, boolean>();
          const allItems: any[] = [];

          for await (const { items } of client.fetchInventoryFull(
            poshmarkUsername,
            { inventoryStatus: ["available"] }
          )) {
            lock.throwIfAborted();
            allItems.push(...items);
            await parallelize(
              items,
              // eslint-disable-next-line no-loop-func
              async (poshmarkItem: (typeof items)[number]) => {
                lock.throwIfAborted();
                if (poshmarkItem.inventory?.status !== "available") return;
                const mappedItems = mapInventory(poshmarkItem);
                const listingId = mappedItems[0].listing_id;
                if (listingId) {
                  const isEmpty = await cache.run(
                    listingId,
                    async (listingId) => {
                      const snapshot = await db
                        .collection("Inventory")
                        .where("user", "==", uid)
                        .where("listing_id", "==", listingId)
                        .get();
                      return snapshot.empty;
                    }
                  );

                  if (!isEmpty) return;
                }
                for (const item of mappedItems) {
                  lock.throwIfAborted();
                  const doc = db.collection("Inventory").doc();
                  await batch.set(
                    doc,
                    stampNormalizedMatchFields({
                      ...item,
                      id: doc.id,
                      user: uid,
                      upload: upload.id,
                      import_type: isInitialImport
                        ? "initial-import"
                        : "direct-import",
                      import_platform: "poshmark",
                      import_date: new Date(),
                    })
                  );
                  if (item.listing_id) importedListingIds.push(item.listing_id);
                  docs++;
                }
              },
              ParalelizeJobs
            );
          }

          lock.throwIfAborted();
          await batch.wait();
          await batch.commit();
          // Flag off: Pending_Inventory has nothing for this user and the
          // extra read must not be able to fail the import.
          if (
            importedListingIds.length &&
            !!featureFlagSelector(getState(), "pendingInventory")
          ) {
            await deletePendingInventoryByListingIds(uid, importedListingIds);
          }
          await uploadFile(filename, JSON.stringify(allItems));

          await upload.update({
            n: docs,
          });
        }
      );
      if (!started) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Poshmark");
        return { error: true };
      }
    } catch (e) {
      logPoshmarkError(e);
      toast.error(poshmarkUserFacingErrorMessage(e));
      return { error: true };
    } finally {
      if (!lockBlocked) dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Poshmark");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}

export function poshmarkSyncSales(
  integrationId: string,
  { start, end }: { start: MonthYear; end: MonthYear }
) {
  return async (
    dispatch,
    getState
  ): Promise<{ success?: boolean; error?: boolean }> => {
    const db = firestore();
    const state = getState();

    if (!isUserAllowedToDirectImport(state, "poshmarkSales"))
      return { error: true };
    const integration = getPoshmarkIntegrations(state)?.find(
      ({ id }) => id === integrationId
    );

    if (!integration || integration.type !== "poshmark" || integration.error)
      return { error: true };

    const extensionId = checkExtensionOrDispatchError(
      dispatch,
      updateSyncStatus,
      "Poshmark",
      "manual"
    );
    if (!extensionId) return { error: true };

    const client = new PoshmarkClient();
    let cookieUserId;
    try {
      cookieUserId = await client.getUserId();
    } catch (e) {
      console.warn(e);
    }

    if (cookieUserId !== integration.uid) {
      dispatch(
        updateSyncStatus({
          type: "error",
          message: "You must be logged in to Poshmark with the correct user",
          code: "user-not-logged-in",
          dispatcher: "manual",
        })
      );
      return { error: true };
    }
    await updateIntegration(integrationId);

    const runningKeys = ["salesIsRunning" as const];
    setBeforeUnloadHandler("Poshmark");

    const isInitialImport = checkIfItIsInitialImport(state, "poshmarkSales");
    const bankAccount = bankAccountByIntegrationIdSelector(
      state,
      integrationId
    );
    let lockBlocked = false;

    try {
      const started = await withSyncLock(
        integrationId,
        runningKeys,
        async (lock) => {
          dispatch(
            updateSyncStatus({
              type: "syncing",
              step: "direct-import-sales",
            })
          );
          // Sync
          const uid = await getUserId();
          const poshmarkUserResponse = await client.fetchUser(integration.uid);
          const poshmarkUserId = poshmarkUserResponse?.data?.id;
          if (!poshmarkUserId) throw new Error("Can't get poshmark user");
          const timestamp = Date.now();
          const filename = `${uid}/poshmarkSales/${timestamp}_${integrationId}.json`;
          const upload = db.collection("Uploads").doc();
          await upload.set({
            user: uid,
            type: "sales",
            provider: "poshmark",
            filename,
            n: 0,
            timestamp,
            id: upload.id,
          });
          const uploadReturns = db.collection("Uploads").doc();
          await uploadReturns.set({
            user: uid,
            type: "returns",
            provider: "poshmark",
            filename,
            n: 0,
            timestamp,
            id: uploadReturns.id,
          });
          let docs = 0;
          let returns = 0;

          const tz = effectiveUserTimeZoneSelector(getState());
          const startDate = startOfMonthInTimeZone(start.year, start.month, tz);
          const endDate = endOfMonthInTimeZone(end.year, end.month, tz);

          const params = {
            boughtDate: {
              start: startDate.toISOString(),
              end: endDate.toISOString(),
            },
          };

          const batch = new FirebaseBatcher();
          const cache = new Cacher<string, boolean>();
          const allSales: any[] = [];

          for await (const { sales } of client.fetchSalesFull(
            poshmarkUserId,
            params
          )) {
            lock.throwIfAborted();
            allSales.push(...sales);
            await parallelize(
              sales,
              // eslint-disable-next-line no-loop-func
              async (sale: (typeof sales)[number]) => {
                lock.throwIfAborted();
                sale.details = await client.fetchSaleDetail(sale.id);
                if (sale.details?.order?.line_items) {
                  for (const lineItem of sale.details.order.line_items) {
                    lineItem.product = await client.fetchInventoryItem(
                      lineItem.product_id
                    );
                  }
                }

                const transformed = mapSale(sale);
                const transactionId = transformed[0].transaction_id;
                if (transactionId) {
                  const isEmpty = await cache.run(
                    transactionId,
                    async (transactionId: string) => {
                      const snapshot = await db
                        .collection("Sales")
                        .where("user", "==", uid)
                        .where("transaction_id", "==", transactionId)
                        .get();
                      return snapshot.empty;
                    }
                  );

                  if (!isEmpty) return;
                }

                for (const i of transformed) {
                  lock.throwIfAborted();
                  const doc = db.collection("Sales").doc();
                  const s = {
                    ...i,
                    id: doc.id,
                    user: uid,
                    upload: upload.id,
                    import_type: isInitialImport
                      ? "initial-import"
                      : "direct-import",
                    import_platform: "poshmark",
                    import_date: new Date(),
                  };

                  if (bankAccount) {
                    s.sale_platform_account_id = bankAccount.id;
                    s.sale_platform = bankAccount.name;
                  }

                  if (
                    sale?.details?.order?.display_status === "Order Cancelled"
                  ) {
                    const r = factorReturnedSale(s, sale);
                    const d = db.collection("Sales").doc();
                    s.return_id = d.id;

                    if (bankAccount) {
                      r.sale_platform_account_id = bankAccount.id;
                      r.sale_platform = bankAccount.name;
                    }

                    await batch.set(
                      d,
                      finalizeSaleComputedFields({
                        ...r,
                        id: d.id,
                        user: uid,
                        upload: uploadReturns.id,
                        import_type: isInitialImport
                          ? "initial-import"
                          : "direct-import",
                        import_platform: "poshmark",
                        import_date: new Date(),
                      })
                    );
                    returns++;
                  }

                  await batch.set(doc, finalizeSaleComputedFields(s));
                  docs++;
                }
              },
              ParalelizeJobs
            );
          }

          lock.throwIfAborted();
          await batch.wait();
          await batch.commit();
          await uploadFile(filename, JSON.stringify(allSales));

          await upload.update({
            n: docs,
          });

          if (returns)
            await uploadReturns.update({
              n: returns,
            });
        }
      );
      if (!started) {
        lockBlocked = true;
        notifySyncAlreadyLocked(dispatch, updateSyncStatus, "Poshmark");
        return { error: true };
      }
    } catch (e) {
      logPoshmarkError(e);
      toast.error(poshmarkUserFacingErrorMessage(e));
      return { error: true };
    } finally {
      if (!lockBlocked) dispatch(updateSyncStatus(undefined));
      clearBeforeUnloadHandler("Poshmark");
    }

    await updateIntegration(integrationId);
    return { success: true };
  };
}
